import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@linaria/react';
import { MAPBOX_DEFAULT_ZOOM, MAPBOX_STYLE_PART } from 'config/map';
import { trackStaticLocationMapBoxDisplayed, trackStaticLocationMapBoxLoaded } from 'middleware/analytics/actions/trackEvent';
import { markAccommodationPageLoad, setStaticMapImageLoaded } from 'modules/accommodation/slice';
import Settings from 'Settings';
import useIsImageloaded from '@daedalus/atlas/hooks/useIsImageloaded';
import { SkeletonLoader } from '@daedalus/atlas/SkeletonLoader';
import { getDevicePixelRatio } from '@daedalus/core/src/_web/utils/browser/getDevicePixelRatio';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/components/TeamContext';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { SelectedElement } from './TrackLocationClick';

// fh-brand-assets bucket, hosted through CloudFront
const pinImagePath = (dpx: string) => `https://d3qk6lo8yfdp1c.cloudfront.net/mapbox/map-pin${dpx}.png`;
interface Props {
  lat: number;
  lon: number;
}
const MapBoxImageWrapper = /*#__PURE__*/styled('div')({
  name: "MapBoxImageWrapper",
  class: "md8mtzq",
  propsAsIs: false
});
const _exp = /*#__PURE__*/() => ({
  backgroundUrl
}) => `url('${backgroundUrl}')`;
const MapBoxImage = /*#__PURE__*/styled('div')({
  name: "MapBoxImage",
  class: "m1wlcd0r",
  propsAsIs: false,
  vars: {
    "m1wlcd0r-0": [_exp()]
  }
});
const baseUrl = 'https://api.mapbox.com/styles/v1';
const accessToken: string = Settings.get('CLIENT_SELECT_MAPBOX_API_KEY');
const useNodeSize = () => {
  const ref = useRef(null);
  const [size, setSize] = useState<{
    width: number;
    height: number;
  }>(null);
  useEffect(() => {
    const node = ref.current;

    // minimum timeout is needed because hidden elements (till overlay slides in have 0,0 size)
    const timeout = setTimeout(() => {
      // Consider using ResizeObserver here when browser support is good enough
      const box = node.getBoundingClientRect();
      // in some rare cases width/height has fractions in their values,
      // which are not allowed in mapbox for image size params (rounding)
      setSize({
        width: Math.round(box.width),
        height: Math.round(box.height)
      });
    });
    return () => {
      clearTimeout(timeout);
    };
  }, []);
  return {
    sizeRef: ref,
    size
  };
};
export const StaticMapImage = ({
  lat,
  lon
}: Props) => {
  const dispatch = useDispatch();
  const {
    sizeRef,
    size
  } = useNodeSize();
  const [mapUrl, setMapUrl] = useState<string>(null);
  const dpr = getDevicePixelRatio(window);
  const {
    isLoaded,
    isErrored
  } = useIsImageloaded(mapUrl);
  const dispatchTeamEvent = useDispatchTeamEvent();
  useEffect(() => {
    if (mapUrl) {
      dispatchTeamEvent(trackStaticLocationMapBoxDisplayed());
    }
  }, [dispatchTeamEvent, mapUrl]);
  useEffect(() => {
    if (isLoaded) {
      dispatch(setStaticMapImageLoaded(true));
      dispatch(markAccommodationPageLoad());
      dispatchTeamEvent(trackStaticLocationMapBoxLoaded());
    }
    if (isErrored) {
      dispatchTeamEvent(trackEvent({
        category: Category.System,
        entity: Entity.MapView,
        action: Action.Errored,
        payload: {
          errorMessage: 'MapBox image failed to load',
          url: mapUrl
        },
        component: 'StaticMap'
      }));
    }
    return () => {
      dispatch(setStaticMapImageLoaded(false));
    };
  }, [dispatchTeamEvent, isLoaded, isErrored, mapUrl]);
  useEffect(() => {
    if (!size) {
      return;
    }

    // MapBox only accepts none or @2x
    const mapPinDprPosfix = dpr > 1.5 ? '@2x' : '';
    const overlay = `url-${encodeURIComponent(pinImagePath(mapPinDprPosfix))}(${lon},${lat})`;
    const lonLatZoom = `${lon},${lat},${MAPBOX_DEFAULT_ZOOM}`;
    const sizePrefix = `${size.width}x${size.height}${mapPinDprPosfix}`;
    if (lon && lat) {
      setMapUrl([baseUrl, MAPBOX_STYLE_PART, 'static', overlay, lonLatZoom, sizePrefix].join('/') + `?access_token=${accessToken}`);
    }
  }, [lon, lat, size, dpr]);
  return <MapBoxImageWrapper ref={sizeRef}>
      {isLoaded ? <MapBoxImage backgroundUrl={mapUrl} data-id={SelectedElement.StaticMapBackground} /> : size && <SkeletonLoader width={size.width} height={size.height} />}
    </MapBoxImageWrapper>;
};

require("./StaticMap.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./StaticMap.tsx");